import { useState, useRef } from 'react';
import { Slide, Component, ComponentType, ComponentContent } from '../../../types/elearning';
import ComponentPalette from './ComponentPalette';
import ComponentEditor from './ComponentEditor';
import ComponentRenderer from '../../elearning/content/ComponentRenderer';
import Button from '../../ui/Button';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

interface SlideBuilderProps {
  slide: Slide;
  onUpdate: (slide: Slide) => void;
}

export default function SlideBuilder({ slide, onUpdate }: SlideBuilderProps) {
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
  const [showPalette, setShowPalette] = useState(false);

  const handleAddComponent = (type: ComponentType) => {
    const newComponent: Component = {
      id: `comp-${Date.now()}`,
      type,
      order: slide.components.length + 1,
      content: getDefaultContent(type),
    };

    const updatedSlide: Slide = {
      ...slide,
      components: [...slide.components, newComponent],
    };

    onUpdate(updatedSlide);
    setSelectedComponentId(newComponent.id);
    setShowPalette(false);
  };

  const handleUpdateComponent = (componentId: string, updatedComponent: Component) => {
    const updatedSlide: Slide = {
      ...slide,
      components: slide.components.map((c) =>
        c.id === componentId ? updatedComponent : c
      ),
    };
    onUpdate(updatedSlide);
  };

  const handleRemoveComponent = (componentId: string) => {
    const updatedSlide: Slide = {
      ...slide,
      components: slide.components
        .filter((c) => c.id !== componentId)
        .map((c, index) => ({ ...c, order: index + 1 })),
    };
    onUpdate(updatedSlide);
    if (selectedComponentId === componentId) {
      setSelectedComponentId(null);
    }
  };

  const handleReorderComponent = (componentId: string, direction: 'up' | 'down') => {
    const currentIndex = slide.components.findIndex((c) => c.id === componentId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= slide.components.length) return;

    const reordered = [...slide.components];
    [reordered[currentIndex], reordered[newIndex]] = [
      reordered[newIndex],
      reordered[currentIndex],
    ];

    const updatedSlide: Slide = {
      ...slide,
      components: reordered.map((c, index) => ({ ...c, order: index + 1 })),
    };
    onUpdate(updatedSlide);
  };

  const selectedComponent = slide.components.find((c) => c.id === selectedComponentId);
  const sortedComponents = [...slide.components].sort((a, b) => a.order - b.order);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Component Palette */}
      <div className="lg:col-span-1">
        <div className="sticky top-4">
          <div className="mb-4">
            <Button
              variant="primary"
              onClick={() => setShowPalette(!showPalette)}
              className="w-full"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              {showPalette ? 'Hide Components' : 'Add Component'}
            </Button>
          </div>

          {showPalette && (
            <div className="mb-4">
              <ComponentPalette onSelectComponent={handleAddComponent} />
            </div>
          )}

          {/* Component List */}
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700">Components</h3>
              {sortedComponents.length > 0 && (
                <span className="text-xs text-gray-500">{sortedComponents.length}</span>
              )}
            </div>
            {sortedComponents.length === 0 ? (
              <div className="text-center py-8 text-gray-400 border border-gray-200 rounded-lg">
                <p className="text-xs">No components</p>
                <p className="text-xs mt-1">Click "Add Component" to start</p>
              </div>
            ) : (
              sortedComponents.map((component) => (
              <div
                key={component.id}
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedComponentId === component.id
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedComponentId(component.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 capitalize">
                      {component.type.replace('-', ' ')}
                    </p>
                    <p className="text-xs text-gray-500">
                      {component.type === 'text' && 'Text block'}
                      {component.type === 'heading' && `Heading ${(component.content as any)?.level || ''}`}
                      {component.type === 'image' && 'Image'}
                      {component.type === 'video' && 'Video'}
                      {component.type === 'card' && (component.content as any)?.title || 'Card'}
                      {component.type === 'callout' && `${(component.content as any)?.type || 'Info'} callout`}
                      {component.type === 'table' && 'Table'}
                      {component.type === 'accordion' && 'Accordion'}
                      {component.type === 'knowledge-check' && 'Knowledge Check'}
                      {component.type === 'resource' && 'Resource'}
                      {component.type === 'columns' && `${(component.content as any)?.columnCount || 2}-column layout`}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm('Are you sure you want to remove this component?')) {
                        handleRemoveComponent(component.id);
                      }
                    }}
                    className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                    aria-label="Remove component"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Preview and Editor - Vertical Layout */}
      <div className="lg:col-span-2 space-y-6">
        {/* Preview */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 min-h-[400px]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Preview</h3>
            {selectedComponent && (
              <span className="text-xs text-primary font-medium">
                Editing: {selectedComponent.type.replace('-', ' ')}
              </span>
            )}
          </div>
          {slide.components.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center text-gray-500">
              <p className="text-sm mb-2">No components yet</p>
              <p className="text-xs">Add components from the palette to see them here</p>
            </div>
          ) : (
            <div className="prose max-w-none">
              <div
                className="preview-container"
                onClick={(e) => {
                  // Don't handle click if user is editing inline
                  if ((e.target as HTMLElement).isContentEditable) {
                    return;
                  }
                  // Find the closest component wrapper
                  const target = e.target as HTMLElement;
                  const componentElement = target.closest('[data-component-id]');
                  if (componentElement) {
                    const componentId = componentElement.getAttribute('data-component-id');
                    if (componentId) {
                      setSelectedComponentId(componentId);
                    }
                  }
                }}
              >
                {slide.components
                  .sort((a, b) => a.order - b.order)
                  .map((component) => {
                    const isHeading = component.type === 'heading';
                    return (
                      <div
                        key={component.id}
                        data-component-id={component.id}
                        className={`${isHeading ? 'group' : 'cursor-pointer'} hover:outline hover:outline-2 hover:outline-primary/50 hover:outline-offset-2 rounded p-2 transition-all mb-2`}
                        title={isHeading ? 'Click the heading to edit it inline, or click elsewhere to open the editor' : 'Click to edit this component'}
                      >
                        {isHeading ? (
                          <EditableComponentRenderer
                            component={component}
                            onUpdate={(updatedContent) => {
                              handleUpdateComponent(component.id, {
                                ...component,
                                content: updatedContent,
                              });
                            }}
                          />
                        ) : (
                          <ComponentRenderer component={component} />
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>

        {/* Component Editor - Below Preview */}
        {selectedComponent ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 overflow-y-auto">
              <div className="flex items-center justify-between mb-4 sticky top-0 bg-gray-50 pb-2 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 capitalize">
                  Edit {selectedComponent.type.replace('-', ' ')}
                </h3>
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => setSelectedComponentId(null)}
                >
                  Close
                </Button>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() =>
                      handleReorderComponent(selectedComponent.id, 'up')
                    }
                    disabled={
                      slide.components.findIndex((c) => c.id === selectedComponent.id) === 0
                    }
                  >
                    ↑ Move Up
                  </Button>
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() =>
                      handleReorderComponent(selectedComponent.id, 'down')
                    }
                    disabled={
                      slide.components.findIndex((c) => c.id === selectedComponent.id) ===
                      slide.components.length - 1
                    }
                  >
                    ↓ Move Down
                  </Button>
                </div>
                <ComponentEditor
                  component={selectedComponent}
                  onUpdate={(updated) =>
                    handleUpdateComponent(selectedComponent.id, updated)
                  }
                  availableSlideComponents={slide.components.filter(c => c.id !== selectedComponent.id)}
                />
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 flex items-center justify-center min-h-[200px]">
              <p className="text-gray-500 text-sm">
                Select a component to edit
              </p>
            </div>
          )}
      </div>
    </div>
  );
}

// Editable component renderer for inline editing
interface EditableComponentRendererProps {
  component: Component;
  onUpdate: (content: ComponentContent) => void;
}

function EditableComponentRenderer({ component, onUpdate }: EditableComponentRendererProps) {
  const contentRef = useRef<HTMLHeadingElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const textContent = (component.content as { text?: string }).text || '';

  const handleClick = () => {
    setIsEditing(true);
    setTimeout(() => {
      if (contentRef.current) {
        const range = document.createRange();
        range.selectNodeContents(contentRef.current);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }, 0);
  };

  const handleBlur = () => {
    setIsEditing(false);
    onUpdate({ ...(component.content as object), text: contentRef.current?.textContent || '' } as ComponentContent);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      contentRef.current?.blur();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      if (contentRef.current) {
        contentRef.current.textContent = textContent;
      }
    }
  };

  const level = (component.content as { level?: number }).level || 2;
  const headingTags = { 1: 'h1', 2: 'h2', 3: 'h3', 4: 'h4', 5: 'h5', 6: 'h6' } as const;
  const headingLevel = (level >= 1 && level <= 6 ? level : 2) as 1 | 2 | 3 | 4 | 5 | 6;
  const HeadingTag = headingTags[headingLevel];
  const placeholder = 'Enter heading text...';

  return (
    <HeadingTag
      ref={contentRef}
      contentEditable={isEditing}
      suppressContentEditableWarning
      onClick={handleClick}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`${isEditing ? 'outline outline-2 outline-primary rounded px-2 py-1 bg-white' : 'cursor-text hover:bg-gray-50 rounded px-2 py-1'} ${!textContent && !isEditing ? 'text-gray-400 italic' : ''}`}
    >
      {textContent ? textContent : (isEditing ? '' : <span className="text-gray-400 italic">{placeholder}</span>)}
    </HeadingTag>
  );
}

export function getDefaultContent(type: ComponentType): any {
  switch (type) {
    case 'text':
      return { html: '' };
    case 'heading':
      return { level: 2, text: '' };
    case 'image':
      return {
        mediaId: 0,
        alt: '',
        alignment: 'center',
        size: 'medium',
      };
    case 'video':
      return {
        youtubeId: '',
        autoplay: false,
      };
    case 'columns':
      return {
        columnCount: 2,
        columns: [{ components: [] }, { components: [] }],
      };
    case 'card':
      return {
        title: '',
        text: '',
        imageId: undefined,
        link: undefined,
        linkText: undefined,
      };
    case 'knowledge-check':
      return {
        question: '',
        type: 'multiple-choice',
        options: [
          { text: '', isCorrect: false },
          { text: '', isCorrect: false },
        ],
        points: 1,
      };
    case 'resource':
      return {
        mediaId: 0,
        fileName: '',
        description: '',
        downloadText: 'Download',
      };
    case 'table':
      return {
        hasHeader: false,
        rows: [
          { cells: [{ content: '' }, { content: '' }] },
          { cells: [{ content: '' }, { content: '' }] },
        ],
      };
    case 'accordion':
      return {
        items: [{ title: '', content: '', defaultExpanded: false }],
      };
    case 'callout':
      return {
        type: 'info',
        title: '',
        content: '',
      };
    default:
      return {};
  }
}

